(() => {
  if (window.__GSWW_VAT_TOGGLE_ACTIVE__) return;
  window.__GSWW_VAT_TOGGLE_ACTIVE__ = true;

  const STORAGE_MODE_KEY = 'gsww_vat_mode';
  const SELECTOR_TOGGLE = '[data-vat-toggle]';
  const SELECTOR_SWITCH = '[data-vat-toggle-switch]';
  const PRICE_TOKEN_DETECT = /[£$€]\s?\d/;
  const PRICE_TOKEN_REGEX = /([£$€])(\s?)(\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)/g;
  const VAT_INCLUSIVE_COPY_PATTERNS = [
    /free\s*(uk\s*)?delivery/i,
    /free\s*shipping/i,
    /orders?\s*over\s*[£$€]?\s*\d/i,
    /on\s+all\s+orders?\s*[£$€]?\s*\d/i,
    /free\s*(delivery|shipping).{0,40}(orders?|spend)/i,
  ];
  const EXCLUDED_CONTAINER_SELECTORS = [
    '[data-vat-exclude]',
    '.free-shipping-goal',
    '.topbar',
    '.topbar-section',
    '.topbar__text',
    '.announcement-bar',
    '.announcement-bar-section',
    '.announcement-bar__content',
    '.announcement-bar__text',
    '[id*="shopify-section-announcement"]',
    '[id*="shopify-section-topbar"]',
  ];
  const DEFAULT_RATE = 20;
  const DEFAULT_BASIS = 'inclusive';
  const DEFAULT_MODE = 'exc';
  let NODE_SOURCE = new WeakMap();
  const PRODUCT_CARD_SELECTOR = '.product-card, .lbcard__product, .lookbook-card, .card-image-with-product';
  const CART_ITEM_SELECTOR = '.cart-item[data-handle][data-variant-id]';
  const CART_SCOPE_SELECTOR = 'cart-items, cart-drawer, .cart-drawer, #MainCartItems, .main-cart, .cart';
  const CART_TOTAL_SELECTOR =
    '.totals, .totals__subtotal-value, .cart__footer--subtotal, .cart-drawer__footer, .drawer__footer';
  const VALID_MODES = new Set(['inc', 'exc']);
  const VALID_BASIS = new Set(['exclusive', 'inclusive']);
  const PRODUCT_TAXABLE_CACHE = new Map();
  const CART_ITEM_TAXABLE_CACHE = new Map();
  const PRODUCT_TAXABLE_PENDING = new Map();
  const PRODUCT_DATA_CACHE = new Map();
  const PRODUCT_DATA_PENDING = new Map();

  const state = {
    ratePercent: DEFAULT_RATE,
    basis: DEFAULT_BASIS,
    mode: DEFAULT_MODE,
    taxable: true,
    isB2B: false,
    isApplying: false,
    rafId: null,
    observer: null,
  };

  function readStorageMode() {
    try {
      const mode = window.localStorage.getItem(STORAGE_MODE_KEY);
      return VALID_MODES.has(mode) ? mode : null;
    } catch (error) {
      return null;
    }
  }

  function writeStorageMode(mode) {
    try {
      window.localStorage.setItem(STORAGE_MODE_KEY, mode);
    } catch (error) {
      // no-op when storage is not available
    }
  }

  function parseBoolean(value, fallback = false) {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }

  function parseNumber(value, fallback) {
    const parsed = Number.parseFloat(String(value || '').trim());
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseDataBoolean(value) {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  }

  function getShopRoot() {
    return window?.Shopify?.routes?.root || '/';
  }

  function getHandleFromUrl(url) {
    if (!url) return null;
    try {
      const parsed = new URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^\/?#]+)/i);
      return match && match[1] ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  }

  function markCardsForHandle(handle, taxable) {
    if (!handle || typeof taxable !== 'boolean') return;
    const links = Array.from(document.querySelectorAll(`a[href*="/products/${handle}"]`));
    links.forEach((link) => {
      const card = link.closest(PRODUCT_CARD_SELECTOR);
      if (!card) return;
      card.setAttribute('data-vat-taxable', taxable ? 'true' : 'false');
      card.dataset.vatHandle = handle;
    });
  }

  function setCartItemsTaxable(handle, variantId, taxable) {
    const items = Array.from(document.querySelectorAll(CART_ITEM_SELECTOR));
    items.forEach((item) => {
      if (String(item.dataset.handle || '') !== String(handle)) return;
      if (Number(item.dataset.variantId) !== Number(variantId)) return;
      item.setAttribute('data-vat-taxable', taxable ? 'true' : 'false');
    });
  }

  function fetchProductDataByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    if (PRODUCT_DATA_CACHE.has(handle)) return Promise.resolve(PRODUCT_DATA_CACHE.get(handle));
    if (PRODUCT_DATA_PENDING.has(handle)) return PRODUCT_DATA_PENDING.get(handle);

    const request = fetch(`${getShopRoot()}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((productData) => {
        PRODUCT_DATA_CACHE.set(handle, productData || null);
        return productData || null;
      })
      .catch(() => null)
      .finally(() => {
        PRODUCT_DATA_PENDING.delete(handle);
      });

    PRODUCT_DATA_PENDING.set(handle, request);
    return request;
  }

  function resolveCardTaxable(card) {
    if (!card) return null;

    const existing = parseDataBoolean(String(card.getAttribute('data-vat-taxable') || '').toLowerCase());
    if (existing != null) return existing;

    const cardLink = card.querySelector('a[href*="/products/"]');
    const handle = getHandleFromUrl(cardLink?.href || '');
    if (!handle) return null;

    card.dataset.vatHandle = handle;

    if (PRODUCT_TAXABLE_CACHE.has(handle)) {
      const cached = PRODUCT_TAXABLE_CACHE.get(handle);
      card.setAttribute('data-vat-taxable', cached ? 'true' : 'false');
      return cached;
    }

    if (PRODUCT_TAXABLE_PENDING.has(handle)) return null;

    const request = fetchProductDataByHandle(handle)
      .then((productData) => {
        if (!productData || !Array.isArray(productData.variants) || !productData.variants.length) return;
        const preferredVariant =
          productData.variants.find((variant) => variant && variant.available) || productData.variants[0];
        const taxable = typeof preferredVariant?.taxable === 'boolean' ? preferredVariant.taxable : true;
        PRODUCT_TAXABLE_CACHE.set(handle, taxable);
        markCardsForHandle(handle, taxable);
        queueApply();
      })
      .catch(() => {
        // no-op
      })
      .finally(() => {
        PRODUCT_TAXABLE_PENDING.delete(handle);
      });

    PRODUCT_TAXABLE_PENDING.set(handle, request);
    return null;
  }

  function resolveCartItemTaxable(cartItem) {
    if (!cartItem) return null;

    const existing = parseDataBoolean(String(cartItem.getAttribute('data-vat-taxable') || '').toLowerCase());
    if (existing != null) return existing;

    const handle = String(cartItem.dataset.handle || '').trim();
    const variantId = Number(cartItem.dataset.variantId);
    if (!handle || !Number.isFinite(variantId)) return null;

    const cacheKey = `${handle}::${variantId}`;
    if (CART_ITEM_TAXABLE_CACHE.has(cacheKey)) {
      const cached = CART_ITEM_TAXABLE_CACHE.get(cacheKey);
      cartItem.setAttribute('data-vat-taxable', cached ? 'true' : 'false');
      return cached;
    }

    if (PRODUCT_DATA_PENDING.has(handle)) return null;

    fetchProductDataByHandle(handle)
      .then((productData) => {
        if (!productData || !Array.isArray(productData.variants) || !productData.variants.length) return;
        const exactVariant = productData.variants.find((variant) => Number(variant?.id) === variantId);
        const fallbackVariant = productData.variants.find((variant) => variant && variant.available) || productData.variants[0];
        const sourceVariant = exactVariant || fallbackVariant;
        if (!sourceVariant) return;
        const taxable = typeof sourceVariant.taxable === 'boolean' ? sourceVariant.taxable : true;
        CART_ITEM_TAXABLE_CACHE.set(cacheKey, taxable);
        setCartItemsTaxable(handle, variantId, taxable);
        queueApply();
      })
      .catch(() => {
        // no-op
      });

    return null;
  }

  function hasTaxIncludedContext() {
    const taxElement = document.querySelector('.product__tax');
    if (!taxElement) return false;
    const text = String(taxElement.textContent || '').trim().toLowerCase();
    return text.includes('tax included');
  }

  function getPrimaryToggle() {
    const productToggle =
      document.querySelector('product-info [data-vat-toggle]') ||
      document.querySelector('.product [data-vat-toggle]') ||
      document.querySelector('.product__info-wrapper [data-vat-toggle]');
    if (productToggle) return productToggle;

    const toggles = Array.from(document.querySelectorAll(SELECTOR_TOGGLE));
    if (!toggles.length) return null;
    if (toggles.length === 1) return toggles[0];

    const inclusiveToggle = toggles.find(
      (toggle) => String(toggle?.dataset?.vatBasis || '').trim().toLowerCase() === 'inclusive'
    );
    return inclusiveToggle || toggles[0];
  }

  function readConfigFromDom() {
    const toggle = getPrimaryToggle();
    if (!toggle) return;

    state.ratePercent = parseNumber(toggle.dataset.vatRate, DEFAULT_RATE);
    const configuredBasis = VALID_BASIS.has(toggle.dataset.vatBasis) ? toggle.dataset.vatBasis : DEFAULT_BASIS;
    state.basis = configuredBasis === 'exclusive' && hasTaxIncludedContext() ? 'inclusive' : configuredBasis;
    state.taxable = parseBoolean(toggle.dataset.vatTaxable, true);
    state.isB2B = parseBoolean(toggle.dataset.vatIsB2b, false);

    const defaultMode = VALID_MODES.has(toggle.dataset.vatDefaultMode) ? toggle.dataset.vatDefaultMode : DEFAULT_MODE;
    const savedMode = readStorageMode();
    const preferred = savedMode || (state.isB2B ? 'exc' : defaultMode);
    state.mode = resolveModeForTaxability(preferred, state.taxable);
  }

  function resolveModeForTaxability(mode, taxable) {
    return VALID_MODES.has(mode) ? mode : DEFAULT_MODE;
  }

  function getVatRateMultiplier() {
    return 1 + Math.max(0, state.ratePercent) / 100;
  }

  function toExclusivePrice(baseValue, basis = state.basis) {
    if (basis === 'inclusive') {
      return baseValue / getVatRateMultiplier();
    }
    return baseValue;
  }

  function convertPrice(baseValue, basis = state.basis, mode = state.mode, taxable = state.taxable) {
    if (!taxable) {
      return baseValue;
    }

    const exclusive = toExclusivePrice(baseValue, basis);
    if (mode === 'inc') {
      return exclusive * getVatRateMultiplier();
    }
    return exclusive;
  }

  function formatPrice(symbol, spacer, sourceAmountText, value, forceTwoDecimals = false) {
    const decimalMatch = sourceAmountText.match(/\.(\d+)/);
    const hasCommaGrouping = sourceAmountText.indexOf(',') > -1;
    const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0;
    let minimumFractionDigits = decimalPlaces;
    let maximumFractionDigits = decimalPlaces;

    if (forceTwoDecimals) {
      minimumFractionDigits = 2;
      maximumFractionDigits = 2;
    } else if (decimalPlaces === 0) {
      minimumFractionDigits = 0;
      maximumFractionDigits = 2;
    }

    const numberFormatter = new Intl.NumberFormat('en-GB', {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping: hasCommaGrouping || value >= 1000,
    });
    return `${symbol}${spacer}${numberFormatter.format(value)}`;
  }

  function replacePricesInText(
    sourceText,
    basis = state.basis,
    forceTwoDecimals = false,
    mode = state.mode,
    taxable = state.taxable
  ) {
    if (!sourceText) return sourceText;

    return sourceText.replace(PRICE_TOKEN_REGEX, (match, symbol, spacer, amountText) => {
      const numeric = Number.parseFloat(amountText.replace(/,/g, ''));
      if (!Number.isFinite(numeric)) return match;
      const converted = convertPrice(numeric, basis, mode, taxable);
      return formatPrice(symbol, spacer || '', amountText, converted, forceTwoDecimals);
    });
  }

  function isKnownRenderedPriceText(sourceText, currentText, taxable = state.taxable) {
    if (currentText === sourceText) return true;
    if (currentText === replacePricesInText(sourceText, state.basis, false, 'exc', taxable)) return true;
    if (currentText === replacePricesInText(sourceText, state.basis, false, 'inc', taxable)) return true;
    return false;
  }

  function getTaxableForNode(node) {
    const parent = node?.parentElement;
    if (!parent || typeof parent.closest !== 'function') return state.taxable;
    const container = parent.closest('[data-vat-taxable]');
    if (container) {
      const parsed = parseDataBoolean(String(container.getAttribute('data-vat-taxable') || '').toLowerCase());
      if (parsed != null) return parsed;
    }

    const cartItem = parent.closest(CART_ITEM_SELECTOR);
    if (cartItem) {
      const resolvedCartTaxable = resolveCartItemTaxable(cartItem);
      if (resolvedCartTaxable != null) return resolvedCartTaxable;
      return null;
    }

    const isInsideCartScope = parent.closest(CART_SCOPE_SELECTOR);
    if (isInsideCartScope) {
      const isCartTotalNode = parent.closest(CART_TOTAL_SELECTOR);
      if (isCartTotalNode) {
        return state.taxable;
      }
      // Never apply fallback conversion inside cart until line-level taxability is explicit.
      // This avoids VAT-free lines being temporarily reduced in Ex VAT mode.
      return null;
    }

    const productCard = parent.closest(PRODUCT_CARD_SELECTOR);
    if (!productCard) return state.taxable;

    const resolved = resolveCardTaxable(productCard);
    return resolved;
  }

  function isVatInclusiveCopy(text) {
    if (!text) return false;
    return VAT_INCLUSIVE_COPY_PATTERNS.some((pattern) => pattern.test(text));
  }

  function hasVatInclusiveContext(element) {
    let current = element;
    let depth = 0;

    while (current && depth < 5) {
      const contextText = (current.textContent || '').trim();
      if (contextText && isVatInclusiveCopy(contextText)) {
        return true;
      }
      current = current.parentElement;
      depth += 1;
    }

    return false;
  }

  function isInExcludedContainer(element) {
    if (!element || typeof element.closest !== 'function') return false;
    return EXCLUDED_CONTAINER_SELECTORS.some((selector) => element.closest(selector));
  }

  function isEligibleTextNode(node) {
    if (!node || node.nodeType !== Node.TEXT_NODE) return false;
    const parent = node.parentElement;
    if (!parent) return false;
    if (parent.closest(SELECTOR_TOGGLE)) return false;
    if (isInExcludedContainer(parent)) return false;
    if (parent.closest('script, style, noscript, template')) return false;

    const text = node.nodeValue;
    if (!text || !text.trim()) return false;
    if (isVatInclusiveCopy(text)) return false;
    if (hasVatInclusiveContext(parent)) return false;
    return PRICE_TOKEN_DETECT.test(text);
  }

  function applyToTextNode(node) {
    if (!isEligibleTextNode(node)) return;
    const taxableForNode = getTaxableForNode(node);
    if (taxableForNode == null) return;

    const currentText = node.nodeValue;
    let sourceText = NODE_SOURCE.get(node);

    if (!sourceText) {
      sourceText = currentText;
      NODE_SOURCE.set(node, sourceText);
    } else if (!isKnownRenderedPriceText(sourceText, currentText, taxableForNode)) {
      // Another script updated this node (for example, cart custom pricing).
      // Re-capture that value as the new source before applying VAT conversion.
      sourceText = currentText;
      NODE_SOURCE.set(node, sourceText);
    }

    const nextText = replacePricesInText(sourceText, state.basis, false, state.mode, taxableForNode);
    if (node.nodeValue !== nextText) {
      node.nodeValue = nextText;
    }
  }

  function resetCapturedSources() {
    NODE_SOURCE = new WeakMap();
  }

  function applyAcrossDocument() {
    if (state.isApplying) return;
    state.isApplying = true;
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);

    let currentNode = walker.nextNode();
    while (currentNode) {
      applyToTextNode(currentNode);
      currentNode = walker.nextNode();
    }

    state.isApplying = false;
  }

  function queueApply() {
    if (state.rafId) return;
    state.rafId = window.requestAnimationFrame(() => {
      state.rafId = null;
      applyAcrossDocument();
      syncToggleUi();
    });
  }

  function syncToggleUi() {
    const toggles = Array.from(document.querySelectorAll(SELECTOR_TOGGLE));
    toggles.forEach((toggle) => {
      const switchButton = toggle.querySelector(SELECTOR_SWITCH);
      if (switchButton) {
        switchButton.disabled = false;
        switchButton.removeAttribute('disabled');
        const isInc = state.mode === 'inc';
        switchButton.setAttribute('aria-checked', String(isInc));
        switchButton.setAttribute(
          'aria-label',
          isInc
            ? 'Inc VAT selected. Click to switch to Ex VAT'
            : 'Ex VAT selected. Click to switch to Inc VAT'
        );
      }
    });
    syncProductVatPriceNotes();
  }

  function syncProductVatPriceNotes() {
    const notes = Array.from(document.querySelectorAll('[data-vat-price-note]'));
    if (!notes.length) return;

    notes.forEach((note) => {
      const incLabel = String(note.dataset.labelInc || '(inc VAT)').trim();
      const excLabel = String(note.dataset.labelExc || '(ex VAT)').trim();
      const freeLabel = String(note.dataset.labelFree || '(VAT Free)').trim();

      let nextLabel = freeLabel;
      if (state.taxable) {
        nextLabel = state.mode === 'inc' ? incLabel : excLabel;
      }

      if (note.textContent !== nextLabel) {
        note.textContent = nextLabel;
      }
    });
  }

  function setMode(nextMode, persist = true) {
    const resolved = resolveModeForTaxability(nextMode, state.taxable);
    if (!VALID_MODES.has(resolved)) return;
    state.mode = resolved;
    if (persist) {
      writeStorageMode(state.mode);
    }
    queueApply();
  }

  function toggleMode() {
    const nextMode = state.mode === 'inc' ? 'exc' : 'inc';
    setMode(nextMode);
  }

  function bindToggleEvents() {
    document.addEventListener('click', (event) => {
      const switchButton = event.target.closest(SELECTOR_SWITCH);
      if (!switchButton) return;
      event.preventDefault();
      toggleMode();
    });
  }

  function bindVariantEvents() {
    document.addEventListener('variant:changed', (event) => {
      const taxable = event?.detail?.variant?.taxable;
      if (typeof taxable === 'boolean') {
        state.taxable = taxable;
        state.mode = resolveModeForTaxability(state.mode, state.taxable);
      }
      queueApply();
    });
  }

  function bindRefreshEvents() {
    // Cart mutations happen frequently and should not reset captured sources,
    // otherwise transformed values can be re-captured and drift.
    ['cart:refresh', 'cart:updated', 'logo-customiser:saved'].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        readConfigFromDom();
        queueApply();
      });
    });

    // Section/block reloads can replace large DOM chunks, so reset sources there.
    ['shopify:section:load', 'shopify:section:reorder', 'shopify:block:select'].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        resetCapturedSources();
        readConfigFromDom();
        queueApply();
      });
    });
  }

  function startObserver() {
    if (state.observer || !document.body) return;
    state.observer = new MutationObserver(() => {
      if (state.isApplying) return;
      queueApply();
    });
    state.observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  function init() {
    if (!getPrimaryToggle()) return;

    readConfigFromDom();
    bindToggleEvents();
    bindVariantEvents();
    bindRefreshEvents();
    startObserver();
    queueApply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
